/** The bar under the thread: the channel selector and Ask AI, which lights up only when the customer spoke last. */

import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type { Channel } from '@/types';
import { ChannelSelector } from './channel-selector';
import type { SimulatorRole } from './roles';

interface ComposerProps {
  disabled: boolean;
  lastNonErrorRole: SimulatorRole | undefined;
  channels: Channel[];
  selectedChannelId?: string;
  onChannelChange?: (id: string) => void;
  onAskAi?: () => void;
}

export const Composer = ({
  disabled,
  lastNonErrorRole,
  channels,
  selectedChannelId,
  onChannelChange,
  onAskAi,
}: ComposerProps) => {
  const askAiEnabled = !disabled && lastNonErrorRole === 'user';

  const handleAskAi = () => {
    if (!askAiEnabled) return;
    onAskAi?.();
  };

  return (
    <div className='relative flex items-center gap-2 rounded-[20px] border border-gray-500 bg-white px-3 py-2.5 shadow-[0px_3px_12px_0px_rgba(0,0,0,0.2)] backdrop-blur-[6px]'>
      <ChannelSelector channels={channels} selectedId={selectedChannelId} onChange={onChannelChange} disabled={disabled} />

      {/* Button first in DOM (row-reverse keeps it visually right) so peer-hover can light the label. */}
      <div className='ml-auto flex flex-row-reverse items-center gap-2'>
        <Button
          size='icon'
          onClick={handleAskAi}
          disabled={!askAiEnabled}
          aria-label='Ask AI'
          className='peer group size-8 rounded-full border border-text-quote bg-ink text-white hover:bg-ink/90 disabled:pointer-events-none disabled:bg-ink/40'
        >
          <ArrowRight
            className='size-[19.2px] transition-transform duration-200 ease-out group-hover:scale-115 motion-reduce:transition-none motion-reduce:group-hover:scale-100'
            strokeWidth={2.6667}
          />
        </Button>
        <span
          className={cn(
            'text-[13px] leading-4.5 font-medium transition-[color,text-shadow] duration-200',
            askAiEnabled
              ? 'text-indigo-600 peer-hover:[text-shadow:0_0_8px_rgba(99,102,241,0.45)]'
              : 'text-text-disabled'
          )}
        >
          Ask AI
        </span>
      </div>
    </div>
  );
};
